import * as THREE from "three";
import { CONFIG } from "../config";
import type { Input } from "../engine/input";
import { CharacterMover, type AABB } from "../engine/collision";
import type { AudioSys } from "../engine/audio";

export class Player {
  mover: CharacterMover;
  yaw = 0;
  pitch = -0.05;
  eye = CONFIG.eyeHeight;
  crouching = false;
  sliding = false;
  sprinting = false;
  ads = false;
  health = CONFIG.health;
  armor = CONFIG.armor;
  alive = true;
  lastHurt = -99;
  bob = 0;
  punch = new THREE.Vector2();
  recoil = new THREE.Vector2();
  fovKick = 0;
  slideTimer = 0;
  private stepAcc = 0;
  private wish = new THREE.Vector3();
  private fwd = new THREE.Vector3();
  private right = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  camera: THREE.PerspectiveCamera;
  dummy = new THREE.Object3D();
  headBob = true;
  sensMul = 1;

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
    this.mover = new CharacterMover(CONFIG.radius, CONFIG.standHeight);
  }

  spawn(p: THREE.Vector3, yaw = 0) {
    this.mover.pos.copy(p);
    this.mover.vel.set(0, 0, 0);
    this.yaw = yaw;
    this.pitch = -0.04;
    this.health = CONFIG.health;
    this.armor = CONFIG.armor;
    this.alive = true;
    this.crouching = false;
    this.sliding = false;
    this.mover.height = CONFIG.standHeight;
    this.eye = CONFIG.eyeHeight;
  }

  hurt(amount: number, from: THREE.Vector3 | null, audio: AudioSys) {
    if (!this.alive) return false;
    let dmg = amount;
    if (this.armor > 0) {
      const absorbed = Math.min(this.armor, dmg * 0.65);
      this.armor -= absorbed;
      dmg -= absorbed;
    }
    this.health -= dmg;
    if (this.health <= 0) {
      this.health = 0;
      this.alive = false;
    }
    this.lastHurt = performance.now() / 1000;
    audio.play("hurt", { vol: 0.55 });
    if (from) {
      return Math.atan2(from.x - this.mover.pos.x, from.z - this.mover.pos.z) - this.yaw;
    }
    return 0;
  }

  update(dt: number, input: Input, boxes: AABB[], audio: AudioSys, time: number) {
    if (!this.alive) {
      this.pitch = THREE.MathUtils.damp(this.pitch, 0.45, 4, dt);
      this.applyCamera(dt);
      return;
    }

    const sens = CONFIG.mouseSens * this.sensMul * (this.ads ? CONFIG.adsSensMul : 1);
    this.yaw -= input.mx * sens;
    this.pitch -= input.my * sens;
    this.pitch = THREE.MathUtils.clamp(this.pitch, -1.25, 1.25);

    const wantCrouch = input.held("ControlLeft") || input.held("KeyC");
    const wantSprint = input.held("ShiftLeft") || input.held("ShiftRight");
    const speedH = Math.hypot(this.mover.vel.x, this.mover.vel.z);

    if (wantCrouch && wantSprint && speedH > CONFIG.slideMinSpeed && this.mover.grounded && !this.sliding) {
      this.sliding = true;
      this.slideTimer = 0.72;
      audio.play("whoosh", { vol: 0.4 });
    }
    if (this.sliding) {
      this.slideTimer -= dt;
      if (this.slideTimer <= 0 || !this.mover.grounded) this.sliding = false;
    }
    this.crouching = wantCrouch || this.sliding;
    const targetH = this.crouching ? CONFIG.crouchHeight : CONFIG.standHeight;
    const targetEye = this.crouching ? CONFIG.crouchEye : CONFIG.eyeHeight;
    this.mover.height = THREE.MathUtils.damp(this.mover.height, targetH, 12, dt);
    this.eye = THREE.MathUtils.damp(this.eye, targetEye, 12, dt);

    this.fwd.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this.wish.set(0, 0, 0);
    if (input.held("KeyW")) this.wish.add(this.fwd);
    if (input.held("KeyS")) this.wish.sub(this.fwd);
    if (input.held("KeyD")) this.wish.add(this.right);
    if (input.held("KeyA")) this.wish.sub(this.right);
    if (this.wish.lengthSq() > 0) this.wish.normalize();

    this.sprinting = wantSprint && !this.ads && !this.crouching && this.wish.dot(this.fwd) > 0.4;
    let maxSp = CONFIG.walkSpeed;
    if (this.sprinting) maxSp = CONFIG.sprintSpeed;
    if (this.crouching && !this.sliding) maxSp = CONFIG.crouchSpeed;
    if (this.ads) maxSp *= 0.78;
    if (this.sliding) maxSp = CONFIG.slideSpeed;

    if (this.mover.grounded) {
      const friction = this.sliding ? 1.6 : CONFIG.friction;
      const sp = Math.hypot(this.mover.vel.x, this.mover.vel.z);
      if (sp > 0.1) {
        const drop = Math.max(sp - friction * dt * sp, 0);
        const m = drop / sp;
        this.mover.vel.x *= m;
        this.mover.vel.z *= m;
      } else {
        this.mover.vel.x = 0;
        this.mover.vel.z = 0;
      }
      const accel = CONFIG.accel;
      const current = this.mover.vel.dot(this.wish);
      const add = THREE.MathUtils.clamp(maxSp - current, 0, accel * maxSp * dt);
      this.mover.vel.addScaledVector(this.wish, add);
      if (this.sliding) this.mover.vel.addScaledVector(this.fwd, dt * 2.2);

      if (input.pressed("Space") && !this.crouching) {
        this.mover.vel.y = CONFIG.jumpSpeed;
        this.mover.grounded = false;
      }
    } else {
      this.mover.vel.y -= CONFIG.gravity * dt;
      const current = this.mover.vel.dot(this.wish);
      const add = THREE.MathUtils.clamp(CONFIG.airAccel - current, 0, CONFIG.airAccel * dt * 8);
      this.mover.vel.addScaledVector(this.wish, add);
    }

    this.mover.move(dt, boxes);

    const moving = Math.hypot(this.mover.vel.x, this.mover.vel.z);
    if (this.mover.grounded && moving > 1.2) {
      this.bob += dt * (this.sprinting ? 11 : 8.2);
      this.stepAcc += dt * moving;
      if (this.stepAcc > (this.sprinting ? 0.38 : 0.48)) {
        this.stepAcc = 0;
        audio.play("step", { vol: this.crouching ? 0.15 : 0.32, rate: this.sprinting ? 1.08 : 1 });
      }
    } else {
      this.bob = THREE.MathUtils.damp(this.bob, 0, 6, dt);
    }

    if (time - this.lastHurt > CONFIG.regenDelay) {
      this.health = Math.min(CONFIG.health, this.health + CONFIG.regenPerSec * dt);
    }

    this.punch.multiplyScalar(Math.exp(-dt * 14));
    this.recoil.x = THREE.MathUtils.damp(this.recoil.x, 0, 8, dt);
    this.recoil.y = THREE.MathUtils.damp(this.recoil.y, 0, 10, dt);
    this.fovKick = THREE.MathUtils.damp(this.fovKick, 0, 8, dt);
    this.applyCamera(dt);
  }

  applyPunch(up: number, horiz: number) {
    this.recoil.x += up;
    this.recoil.y += horiz;
    this.punch.x += horiz * 0.35;
    this.punch.y += up * 0.4;
    this.fovKick += 1.6;
  }

  applyCamera(dt: number) {
    const bobX = this.headBob ? Math.sin(this.bob) * 0.025 : 0;
    const bobY = this.headBob ? Math.abs(Math.cos(this.bob)) * 0.03 : 0;
    this.camera.position.set(
      this.mover.pos.x + bobX * Math.cos(this.yaw),
      this.mover.pos.y + this.eye + bobY - (this.sliding ? 0.12 : 0),
      this.mover.pos.z + bobX * Math.sin(this.yaw)
    );
    this.camera.quaternion.setFromEuler(
      new THREE.Euler(
        this.pitch - this.recoil.x * 0.04 - this.punch.y,
        this.yaw - this.recoil.y * 0.03 - this.punch.x,
        this.sliding ? 0.08 : bobX * 0.4,
        "YXZ"
      )
    );
    const base = this.ads ? CONFIG.adsFov : CONFIG.worldFov;
    this.camera.fov = THREE.MathUtils.damp(this.camera.fov, base + this.fovKick, 14, dt);
    this.camera.updateProjectionMatrix();
    this.dummy.position.copy(this.camera.position);
    this.dummy.quaternion.copy(this.camera.quaternion);
  }

  forward(out: THREE.Vector3) {
    return this.camera.getWorldDirection(out);
  }
}
